import { clockTime, spanText, usdText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { HeldIcon } from '../../shared/ui/icons';
import { Meter } from '../../shared/ui/meter';
import { SectionPanel } from '../../shared/ui/section-panel';
import type { TAiBudgetToday } from '../../entities/organization';

const DAY_MS = 86_400_000;

// "at today's pace it would run out around 19:40": spend per elapsed minute since 00:00 UTC.
const runsOutAt = (today: TAiBudgetToday, now: number): string | null => {
  const dayStart = Date.parse(today.resetsAt) - DAY_MS;
  const elapsed = now - dayStart;
  if (today.spentUsd <= 0 || elapsed <= 0) return null;
  const at = dayStart + (today.limitUsd / today.spentUsd) * elapsed;
  return at < Date.parse(today.resetsAt) ? new Date(at).toISOString() : null;
};

// Board 37 "5 copies · 2 agent runs": an agent's held model call is a queue row too.
const heldWords = (today: TAiBudgetToday): string => {
  const copies = Math.max(0, today.waiting - today.heldAgentRuns);
  const words = `${copies.toLocaleString('en-US')} ${copies === 1 ? 'copy' : 'copies'} on AI steps`;
  if (today.heldAgentRuns === 0) return words;
  return `${words} · ${today.heldAgentRuns.toLocaleString('en-US')} agent ${today.heldAgentRuns === 1 ? 'run' : 'runs'}`;
};

interface ITodayCardProps {
  today: TAiBudgetToday;
  ranOutAt: string | null;
  now: number;
  timeZone?: string;
  editable: boolean;
  onRaise: () => void;
}

/** Board S4, "Today": runs and tests together against the daily budget. */
export const TodayCard = ({
  today,
  ranOutAt,
  now,
  timeZone,
  editable,
  onRaise,
}: ITodayCardProps) => {
  const off = today.limitUsd <= 0;
  const spent = !off && today.spentUsd >= today.limitUsd;
  const share = off ? 0 : Math.min(100, (today.spentUsd / today.limitUsd) * 100);
  const pace = !off && !spent ? runsOutAt(today, now) : null;
  const reset = clockTime(today.resetsAt, 'UTC');

  return (
    <SectionPanel
      title="Today"
      note={`runs and tests together · resets at ${reset} UTC`}
      actions={
        spent ? (
          <span className="inline-flex items-center gap-label text-meta text-primary">
            <HeldIcon size="sm" className="text-muted" />
            Spent
          </span>
        ) : undefined
      }
    >
      <div className="px-card py-4">
        <div className="flex items-end gap-4">
          <span className="text-display font-semibold tracking-display text-primary">
            {usdText(today.spentUsd)}
          </span>
          <span className="pb-1 text-data text-muted">
            {off
              ? 'AI steps are off: the budget is $0.00'
              : `of ${usdText(today.limitUsd)}${spent && ranOutAt ? ` · ran out at ${clockTime(ranOutAt, timeZone)}` : ''}`}
          </span>
        </div>
        <Meter value={share} tone={spent ? 'accent' : 'motion'} className="mt-4" />
        <div className="mt-3 flex items-center gap-3">
          <p className="min-w-0 flex-1 font-mono text-micro text-faint">
            {spent
              ? `held until ${reset} UTC, in ${spanText(Date.parse(today.resetsAt) - now)}: ${heldWords(today)}`
              : off
                ? `copies that reach an AI step wait there until the budget is above $0.00 · ${today.waiting.toLocaleString('en-US')} waiting`
                : pace
                  ? `at today’s pace it would run out around ${clockTime(pace, timeZone)}`
                  : 'at today’s pace it lasts the day'}
          </p>
          {(spent || off) && editable && (
            <Button size="sm" onClick={onRaise}>
              Raise today’s budget
            </Button>
          )}
        </div>
      </div>
    </SectionPanel>
  );
};
